/*
  SD shoppable videos (v2, 23 Oct 2026). Works with sections/sd-shoppable-videos.liquid and assets/sd-videos.css.
  Layouts: story, carousel, highlighted, grid (cards play muted 480p previews while on screen) and float (a draggable
  bubble that grows into a mini player, then opens the full player).
  The full player moves to <body> so no section transform clips it. It sits at z-index 998, under the theme's drawers
  (999), so "Add" opens the rewards cart drawer on top of the video. The video pauses while the drawer is open.
*/
(() => {
  if (window.customElements.get('sd-reels')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  let soundOn = true; // shared by every player on the page; opening is a tap, so sound is allowed

  const store = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (e) { /* private mode */ } },
  };

  const playQuietly = (video) => {
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  };

  class SdReels extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-sd-viewer]');
      if (!this.viewer) return;
      this.layout = this.dataset.layout || 'carousel';
      this.track = this.viewer.querySelector('[data-sd-track]');
      this.slides = Array.from(this.track.querySelectorAll('[data-sd-slide]'));
      this.countLabel = this.viewer.querySelector('[data-sd-count]');
      this.muteBtn = this.viewer.querySelector('[data-sd-mute]');
      this.toast = this.viewer.querySelector('[data-sd-toast]');
      this.active = -1;
      this.isOpen = false;
      this.ownsHistory = false;
      this.abort = new AbortController();
      const signal = this.abort.signal;

      document.body.appendChild(this.viewer);

      this.querySelectorAll('[data-sd-open]').forEach((card) => {
        card.querySelector('button')?.addEventListener('click', () => this.open(Number(card.dataset.sdOpen)), { signal });
      });

      this.viewer.addEventListener('click', (event) => this.onViewerClick(event), { signal });
      this.viewer.addEventListener('change', (event) => this.onSizeChange(event), { signal });
      this.viewer.addEventListener('variant:add', () => this.pauseActive(), { signal });
      document.addEventListener('keydown', (event) => this.onKey(event), { signal });
      window.addEventListener('popstate', () => { if (this.isOpen && this.ownsHistory) { this.ownsHistory = false; this.close(); } }, { signal });

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.addEventListener('timeupdate', () => {
          const bar = slide.querySelector('.sd-reel__progress span');
          if (bar && video.duration) bar.style.transform = `scaleX(${video.currentTime / video.duration})`;
        }, { signal });
        video.addEventListener('play', () => slide.classList.remove('is-paused'), { signal });
        video.addEventListener('pause', () => slide.classList.add('is-paused'), { signal });
      });

      // Keep the title and caption clear of the product sheet on phones.
      this.sheetObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.closest('[data-sd-slide]')?.style.setProperty('--sd-shop-h', `${Math.ceil(entry.target.offsetHeight)}px`);
        });
      });
      this.slides.forEach((slide) => {
        const shop = slide.querySelector('.sd-reel__shop');
        if (shop) this.sheetObserver.observe(shop);
      });

      // Which video is on screen: every slide is one track-height tall, so the scroll position tells us.
      let settle;
      this.track.addEventListener('scroll', () => {
        if (!this.isOpen) return;
        clearTimeout(settle);
        settle = setTimeout(() => this.activate(this.indexFromScroll()), 90);
      }, { passive: true, signal });

      this.setupPreviews(signal);
      if (this.layout === 'float') this.setupFloat(signal);
      this.openFromLink();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.sheetObserver?.disconnect();
      this.previewObserver?.disconnect();
      if (this.isOpen) this.unlockScroll();
      this.viewer?.remove(); // it lives in <body>; the theme editor re-renders the section
      this.float?.remove();
    }

    /* ---------- Card previews (carousel, highlighted, grid) ---------- */

    setupPreviews(signal) {
      this.previews = Array.from(this.querySelectorAll('.sd-vcard__video'));
      if (!this.previews.length || this.dataset.previews !== 'true' || reduceMotion || saveData) return;

      const start = (video) => {
        if (this.isOpen) return;
        // Start 1.5 s in: the uploads fade in from black. The cover stays until frames actually play.
        if (!video.src) {
          video.src = `${video.dataset.src}#t=1.5`;
          video.addEventListener('playing', () => video.closest('.sd-vcard')?.classList.add('is-previewing'));
        } else {
          video.closest('.sd-vcard')?.classList.add('is-previewing');
        }
        playQuietly(video);
      };
      const stop = (video) => {
        video.pause();
        video.closest('.sd-vcard')?.classList.remove('is-previewing');
      };
      this.startPreview = start;
      this.stopPreview = stop;

      if (this.layout === 'highlighted') {
        // Only the card nearest the centre of the row plays, and it is drawn larger.
        const row = this.querySelector('[data-sd-row]');
        const pick = () => {
          const mid = row.getBoundingClientRect().left + row.clientWidth / 2;
          let best = null;
          let bestDistance = Infinity;
          row.querySelectorAll('.sd-vcard').forEach((card) => {
            const r = card.getBoundingClientRect();
            const d = Math.abs(r.left + r.width / 2 - mid);
            if (d < bestDistance) { bestDistance = d; best = card; }
          });
          row.querySelectorAll('.sd-vcard').forEach((card) => {
            const video = card.querySelector('.sd-vcard__video');
            card.classList.toggle('is-center', card === best);
            if (!video) return;
            if (card === best && this.rowVisible) start(video);
            else stop(video);
          });
        };
        let timer;
        row.addEventListener('scroll', () => { clearTimeout(timer); timer = setTimeout(pick, 80); }, { passive: true, signal });
        this.previewObserver = new IntersectionObserver(([entry]) => {
          this.rowVisible = entry.isIntersecting;
          pick();
        }, { threshold: 0.4 });
        this.previewObserver.observe(row);
        this.repickPreviews = pick;
        // Start with the first card centred.
        row.querySelector('.sd-vcard')?.classList.add('is-center');
        return;
      }

      this.previewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('.sd-vcard__video');
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) start(video);
          else stop(video);
        });
      }, { threshold: [0, 0.6] });
      this.querySelectorAll('.sd-vcard').forEach((card) => this.previewObserver.observe(card));
    }

    pausePreviews() {
      this.previews?.forEach((video) => video.pause());
    }

    resumePreviews() {
      if (!this.stopPreview) return;
      if (this.repickPreviews) { this.repickPreviews(); return; }
      this.querySelectorAll('.sd-vcard.is-previewing .sd-vcard__video').forEach((video) => playQuietly(video));
    }

    /* ---------- Float ---------- */

    setupFloat(signal) {
      this.float = this.querySelector('[data-sd-float]');
      if (!this.float || !this.slides.length) return;
      if (store.get('sdFloatClosed') === '1') { this.float.remove(); this.float = null; return; }

      document.body.appendChild(this.float);
      this.floatBody = this.float.querySelector('[data-sd-float-body]');
      this.floatVideo = this.float.querySelector('.sd-float__video');
      this.floatIndex = 0;
      this.floatSound = false;
      this.float.hidden = false;
      this.loadFloat(0);

      // Restore the spot the visitor dragged it to (this visit only).
      const saved = store.get('sdFloatPos');
      if (saved) {
        try {
          const pos = JSON.parse(saved);
          this.floatSide = pos.side;
          this.floatY = pos.y;
        } catch (e) { /* ignore */ }
      }
      this.floatSide = this.floatSide || this.float.dataset.side || 'left';
      this.placeFloat();
      window.addEventListener('resize', () => this.placeFloat(), { signal });

      this.floatVideo.addEventListener('playing', () => this.float?.classList.add('is-playing'), { signal });

      // Play through the videos one after another while the bubble is up.
      this.floatVideo.addEventListener('ended', () => this.loadFloat((this.floatIndex + 1) % this.slides.length, true), { signal });

      this.float.addEventListener('click', (event) => this.onFloatClick(event), { signal });
      this.setupDrag(signal);
    }

    loadFloat(index, autoplay = true) {
      const slide = this.slides[index];
      if (!slide || !this.floatVideo) return;
      this.floatIndex = index;
      this.floatVideo.poster = slide.dataset.poster || '';
      this.floatBody.style.backgroundImage = slide.dataset.poster ? `url("${slide.dataset.poster}")` : '';
      this.float.classList.remove('is-playing');
      this.floatVideo.src = slide.dataset.preview ? `${slide.dataset.preview}#t=1.5` : '';
      this.floatVideo.muted = !this.floatSound;
      const pill = this.float.querySelector('[data-sd-float-shop]');
      if (pill) {
        pill.querySelector('.sd-float__pill-name').textContent = slide.dataset.productTitle || '';
        pill.querySelector('.sd-float__pill-price').textContent = slide.dataset.productPrice || '';
        const img = pill.querySelector('.sd-float__pill-img');
        if (slide.dataset.productImg) { img.src = slide.dataset.productImg; img.hidden = false; } else { img.hidden = true; }
      }
      if (autoplay && !reduceMotion) playQuietly(this.floatVideo);
    }

    onFloatClick(event) {
      if (this.dragJustEnded) return; // the click that ends a drag is not a tap
      const t = event.target;
      if (t.closest('[data-sd-float-close]')) {
        store.set('sdFloatClosed', '1');
        this.floatVideo.pause();
        this.float.remove();
        this.float = null;
        return;
      }
      if (t.closest('[data-sd-float-min]')) { this.setFloatSize(false); return; }
      if (t.closest('[data-sd-float-sound]')) {
        this.floatSound = !this.floatSound;
        this.floatVideo.muted = !this.floatSound;
        this.float.classList.toggle('is-sound', this.floatSound);
        t.closest('[data-sd-float-sound]').setAttribute('aria-pressed', String(this.floatSound));
        playQuietly(this.floatVideo);
        return;
      }
      if (t.closest('[data-sd-float-expand], [data-sd-float-shop]')) { this.openFromFloat(); return; }
      if (t.closest('[data-sd-float-open]')) {
        // First tap grows the bubble into the mini player; a tap on the mini player opens the full player.
        if (!this.float.classList.contains('is-mini')) {
          this.setFloatSize(true);
          this.floatSound = true;
          this.floatVideo.muted = false;
          this.float.classList.add('is-sound');
          this.float.querySelector('[data-sd-float-sound]')?.setAttribute('aria-pressed', 'true');
          playQuietly(this.floatVideo);
        } else {
          this.openFromFloat();
        }
      }
    }

    openFromFloat() {
      this.floatVideo.pause();
      this.float.classList.add('is-away');
      this.open(this.floatIndex);
    }

    setFloatSize(mini) {
      this.float.classList.toggle('is-mini', mini);
      this.placeFloat();
    }

    placeFloat() {
      if (!this.float) return;
      const margin = 16;
      const w = this.floatBody.offsetWidth;
      const h = this.floatBody.offsetHeight;
      const maxY = window.innerHeight - h - margin;
      const y = Math.min(maxY, Math.max(margin + 60, this.floatY ?? maxY - 70));
      const x = this.floatSide === 'right' ? window.innerWidth - w - margin : margin;
      this.float.style.transform = `translate(${x}px, ${y}px)`;
      this.float.dataset.side = this.floatSide;
      this.floatX = x;
      this.floatY = y;
    }

    setupDrag(signal) {
      const body = this.floatBody;
      let start = null;
      body.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest('.sd-float__btn')) return;
        start = { x: event.clientX, y: event.clientY, fx: this.floatX, fy: this.floatY, id: event.pointerId };
        this.dragMoved = false;
      }, { signal });
      window.addEventListener('pointermove', (event) => {
        if (!start || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (!this.dragMoved && Math.hypot(dx, dy) < 8) return;
        if (!this.dragMoved) {
          this.dragMoved = true;
          this.float.classList.add('is-dragging');
          try { body.setPointerCapture(start.id); } catch (e) { /* ignore */ }
        }
        event.preventDefault();
        this.float.style.transform = `translate(${start.fx + dx}px, ${start.fy + dy}px)`;
      }, { signal, passive: false });
      const end = (event) => {
        if (!start || (event && event.pointerId !== start.id)) return;
        if (this.dragMoved) {
          const r = body.getBoundingClientRect();
          this.floatSide = r.left + r.width / 2 > window.innerWidth / 2 ? 'right' : 'left';
          this.floatY = r.top;
          this.float.classList.remove('is-dragging');
          this.placeFloat();
          store.set('sdFloatPos', JSON.stringify({ side: this.floatSide, y: this.floatY }));
          this.dragJustEnded = true;
          setTimeout(() => { this.dragJustEnded = false; }, 60);
        }
        this.dragMoved = false;
        start = null;
      };
      window.addEventListener('pointerup', end, { signal });
      window.addEventListener('pointercancel', end, { signal });
      // Touch scrolling must not start while the bubble is being dragged.
      body.addEventListener('touchmove', (event) => { if (this.dragMoved) event.preventDefault(); }, { signal, passive: false });
    }

    /* ---------- Full player ---------- */

    open(index) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.pausePreviews();
      this.viewer.hidden = false;
      this.lockScroll();
      this.updateMute();
      void this.viewer.offsetHeight; // lay out now so the fade-in runs and offsetTop is real
      this.viewer.classList.add('is-open');
      const target = this.slides[index] || this.slides[0];
      this.track.scrollTo({ top: target.offsetTop, behavior: 'instant' });
      this.activate(this.slides.indexOf(target)); // play inside the tap, so the browser allows sound
      this.viewer.querySelector('[data-sd-close]')?.focus({ preventScroll: true });
      try {
        history.pushState({ sdReels: true }, '');
        this.ownsHistory = true;
      } catch (e) { this.ownsHistory = false; }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.slides.forEach((slide) => slide.querySelector('video')?.pause());
      const last = this.active;
      this.active = -1;
      this.viewer.classList.remove('is-open');
      this.viewer.hidden = true;
      this.unlockScroll();
      if (this.ownsHistory) {
        this.ownsHistory = false;
        history.back();
      }
      if (this.float) {
        // Back to the mini player, muted: the visitor just left the sound-on player.
        this.floatSound = false;
        this.floatVideo.muted = true;
        this.float.classList.remove('is-away', 'is-sound');
        this.float.querySelector('[data-sd-float-sound]')?.setAttribute('aria-pressed', 'false');
        if (last >= 0 && last !== this.floatIndex) this.loadFloat(last);
        else playQuietly(this.floatVideo);
      }
      this.resumePreviews();
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    openFromLink() {
      let handle = null;
      try { handle = new URLSearchParams(location.search).get('sd_video'); } catch (e) { return; }
      if (!handle || window.sdVideoLinkOpened) return;
      const index = this.slides.findIndex((slide) => slide.dataset.video === handle);
      if (index < 0) return;
      window.sdVideoLinkOpened = true; // several video sections on one page: only the first opens it
      // No tap yet, so the browser only allows muted playback; the sound button turns it on.
      soundOn = false;
      if (this.float) this.float.classList.add('is-away');
      this.open(index);
    }

    activate(index) {
      if (index < 0 || index === this.active) return;
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        slide.classList.toggle('is-active', i === index);
        if (i !== index && video) video.pause();
      });
      this.active = index;
      const shop = this.slides[index].querySelector('.sd-reel__shop');
      if (shop) this.slides[index].style.setProperty('--sd-shop-h', `${Math.ceil(shop.offsetHeight)}px`);
      if (this.countLabel) this.countLabel.textContent = `${index + 1} / ${this.slides.length}`;
      this.viewer.classList.toggle('is-first', index === 0);
      this.viewer.classList.toggle('is-last', index === this.slides.length - 1);
      this.play(this.slides[index]);
      this.warm(index + 1);
    }

    play(slide) {
      const video = slide?.querySelector('video');
      if (!video) return;
      video.muted = !soundOn;
      if (video.preload === 'none') video.preload = 'auto';
      const attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {
          // Browser refused sound: play muted rather than not at all.
          soundOn = false;
          this.updateMute();
          video.muted = true;
          video.play().catch(() => slide.classList.add('is-paused'));
        });
      }
    }

    indexFromScroll() {
      const h = this.track.clientHeight || 1;
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollTop / h)));
    }

    warm(index) {
      const video = this.slides[index]?.querySelector('video');
      if (video && video.preload === 'none') video.preload = 'metadata';
    }

    pauseActive() {
      this.slides[this.active]?.querySelector('video')?.pause();
    }

    go(step) {
      const next = Math.min(this.slides.length - 1, Math.max(0, this.active + step));
      const target = this.slides[next];
      if (target) this.track.scrollTo({ top: target.offsetTop, behavior: reduceMotion ? 'instant' : 'smooth' });
      // Some browsers skip scroll events on programmatic scrolls; make sure the right video plays.
      setTimeout(() => this.activate(this.indexFromScroll()), reduceMotion ? 0 : 450);
    }

    updateMute() {
      if (!this.muteBtn) return;
      this.muteBtn.setAttribute('aria-pressed', String(!soundOn));
      this.viewer.classList.toggle('is-muted', !soundOn);
    }

    async share(slide) {
      const url = new URL(location.href);
      url.searchParams.set('sd_video', slide.dataset.video);
      const data = { title: slide.getAttribute('aria-label') || document.title, url: url.toString() };
      try {
        if (navigator.share) { await navigator.share(data); return; }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
      try {
        await navigator.clipboard.writeText(data.url);
      } catch (e) { /* clipboard blocked */ }
      if (this.toast) {
        this.toast.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => { this.toast.hidden = true; }, 1800);
      }
    }

    onViewerClick(event) {
      const t = event.target;
      if (t.closest('[data-sd-close]')) return this.close();
      if (t.closest('[data-sd-next]')) return this.go(1);
      if (t.closest('[data-sd-prev]')) return this.go(-1);
      if (t.closest('[data-sd-share]')) return this.share(t.closest('[data-sd-slide]'));
      if (t.closest('[data-sd-mute]')) {
        soundOn = !soundOn;
        this.updateMute();
        const video = this.slides[this.active]?.querySelector('video');
        if (video) video.muted = !soundOn;
        return;
      }
      const toggle = t.closest('[data-sd-toggle]');
      if (toggle) {
        const video = toggle.closest('[data-sd-slide]')?.querySelector('video');
        if (!video) return;
        if (video.paused) this.play(toggle.closest('[data-sd-slide]'));
        else video.pause();
        return;
      }
      // Desktop: a click on the dimmed backdrop around the card closes the player.
      if (t === this.viewer || t.classList.contains('sd-reel')) this.close();
    }

    onSizeChange(event) {
      const select = event.target.closest('.sd-reel-product__size');
      if (!select) return;
      const option = select.selectedOptions[0];
      const card = select.closest('.sd-reel-product');
      const price = card?.querySelector('[data-sd-price]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      let compare = card?.querySelector('[data-sd-compare]');
      if (option?.dataset.compare) {
        if (!compare) {
          compare = document.createElement('s');
          compare.setAttribute('data-sd-compare', '');
          price.after(compare);
        }
        compare.textContent = option.dataset.compare;
        compare.hidden = false;
      } else if (compare) {
        compare.hidden = true;
      }
    }

    onKey(event) {
      if (!this.isOpen) return;
      // Leave keys alone while a theme drawer (the cart) is open on top of the viewer.
      if (document.querySelector('.drawer[open], cart-drawer[open]')) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      else if (['ArrowDown', 'ArrowRight', 'PageDown'].includes(event.key)) { event.preventDefault(); this.go(1); }
      else if (['ArrowUp', 'ArrowLeft', 'PageUp'].includes(event.key)) { event.preventDefault(); this.go(-1); }
      else if (event.key === ' ' && event.target === document.body) {
        event.preventDefault();
        const video = this.slides[this.active]?.querySelector('video');
        if (video) video.paused ? this.play(this.slides[this.active]) : video.pause();
      }
    }

    lockScroll() {
      document.documentElement.classList.add('sd-reels-lock');
      this.hideFloaters();
    }

    unlockScroll() {
      document.documentElement.classList.remove('sd-reels-lock');
      this.restoreFloaters();
    }

    // Chat bubbles and other app widgets float above everything (z-index 10000+) and some re-render themselves,
    // so they are hidden with a temporary style rule on their id or classes rather than an inline style.
    // Theme drawers and popovers stay, so the cart drawer still opens on top.
    hideFloaters() {
      const selectors = new Set();
      document.querySelectorAll('body *').forEach((el) => {
        if (el === this.viewer || this.viewer.contains(el)) return;
        if (el.closest('.drawer, .popover, dialog, cart-drawer, [role="dialog"], sd-reels, .sd-float')) return;
        const style = getComputedStyle(el);
        if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') return;
        if ((parseInt(style.zIndex, 10) || 0) < 900) return;
        if (el.id) selectors.add(`#${CSS.escape(el.id)}`);
        else if (el.classList.length) selectors.add(`${el.tagName.toLowerCase()}.${Array.from(el.classList).map((c) => CSS.escape(c)).join('.')}`);
      });
      if (!selectors.size) return;
      this.floaterStyle = document.createElement('style');
      this.floaterStyle.textContent = `${Array.from(selectors).join(',')}{visibility:hidden!important;}`;
      document.head.appendChild(this.floaterStyle);
    }

    restoreFloaters() {
      this.floaterStyle?.remove();
      this.floaterStyle = null;
    }
  }

  window.customElements.define('sd-reels', SdReels);
})();
